import { jitsiLocalStorage } from '@jitsi/js-utils';

import { toggleDialog } from '../base/dialog/actions';
import i18next, { DEFAULT_LANGUAGE } from '../base/i18n/i18next';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE, showNotification } from '../notifications';

import {
    REMOVE_CACHED_TRANSCRIPT_MESSAGE,
    REMOVE_TRANSCRIPT_MESSAGE,
    SET_CAPTIONS_VIEW,
    SET_DEFAULT_TRANSCRIPTION_LANGUAGE,
    SET_REQUESTING_SUBTITLES,
    SET_TRANSLATED_AUDIO_TRACK,
    SET_TRANSLATED_VOICE_ENABLED,
    SET_VOICE_CAPABILITY,
    SET_VOICE_CATALOG,
    SET_VOICE_MODE_STATE,
    TOGGLE_REQUESTING_CAPTIONS,
    TOGGLE_REQUESTING_SUBTITLES,
    UPDATE_TRANSCRIPT_MESSAGE
} from './actionTypes';
import LanguageSelectorDialog from './components/web/LanguageSelectorDialog.web';
import { CLONE_RECORDING_SECONDS, INWORLD_CLONE_PATH } from './constants';
import {
    getInworldCloneConfig,
    getSelectedTranslationLanguage,
    getStoredCloneVoiceId,
    getTranslatedVoiceUnavailableReason,
    isCloneVoiceSelected,
    setCloneVoiceSelected,
    setStoredCloneVoiceId
} from './functions.web';
import logger from './logger';

const VOICE_CLIENT_PERSISTENCE_ID_KEY = 'voiceClientPersistenceId';

/**
 * Creates a best-effort stable identifier for this browser client.
 *
 * @returns {string}
 */
function generatePersistenceId() {
    const cryptoObj = window?.crypto;

    if (cryptoObj?.randomUUID) {
        return cryptoObj.randomUUID();
    }

    return `voice-${Date.now()}-${Math.random().toString(36)
        .slice(2)}`;
}

/**
 * Gets the existing browser persistence id or creates one in local storage.
 *
 * @returns {string|undefined}
 */
function getOrCreateVoiceClientPersistenceId() {
    try {
        let id = jitsiLocalStorage.getItem(VOICE_CLIENT_PERSISTENCE_ID_KEY);

        if (!id) {
            id = generatePersistenceId();
            jitsiLocalStorage.setItem(VOICE_CLIENT_PERSISTENCE_ID_KEY, id);
        }

        return id || undefined;
    } catch {
        // localStorage may be blocked in strict browser contexts.
        return undefined;
    }
}

/**
 * Signals that a cached transcript has to be removed from the state.
 *
 * @param {string} transcriptMessageID - The message_id to be removed.
 * @returns {{
*      type: REMOVE_CACHED_TRANSCRIPT_MESSAGE,
*      transcriptMessageID: string,
* }}
*/
export function removeCachedTranscriptMessage(transcriptMessageID: string) {
    return {
        type: REMOVE_CACHED_TRANSCRIPT_MESSAGE,
        transcriptMessageID
    };
}

// /**
//  * Signals that a participant sent an endpoint message on the data channel.
//  *
//  * @param {Object} participant - The participant details sending the message.
//  * @param {Object} json - The json carried by the endpoint message.
//  * @returns {{
//  *      type: ENDPOINT_MESSAGE_RECEIVED,
//  *      participant: Object,
//  *      json: Object
//  * }}
//  */
// export function endpointMessageReceived(participant: Object, json: Object) {
//     return {
//         type: ENDPOINT_MESSAGE_RECEIVED,
//         participant,
//         json
//     };
// }

/**
 * Signals that the local user has toggled the ClosedCaption button.
 *
 * @returns {{
 *      type: TOGGLE_REQUESTING_SUBTITLES
 * }}
 */
export function toggleRequestingSubtitles() {
    return {
        type: TOGGLE_REQUESTING_SUBTITLES
    };
}

/**
 * Open and close Captions Modal ClosedCaption button.
 *
 * @returns {{
 *      type: TOGGLE_REQUESTING_CAPTIONS
 * }}
 */
export function closeCaptionsModal(requestingCaptionsModal: Boolean) {
    return {
        type: TOGGLE_REQUESTING_CAPTIONS,
        requestingCaptionsModal
    };
}

/**
 * Docks ('fixed') or undocks ('float') the captions panel and remembers the
 * choice. Independent of the conference view.
 *
 * @param {string} captionsView - Either 'fixed' or 'float'.
 * @returns {{
 *      type: SET_CAPTIONS_VIEW,
 *      captionsView: string
 * }}
 */
export function setCaptionsView(captionsView: string) {
    jitsiLocalStorage.setItem('captionsView', captionsView);

    return {
        type: SET_CAPTIONS_VIEW,
        captionsView
    };
}

/**
 * Signals that the local user has toggled the LanguageSelector button.
 *
 * @returns {Function}
 */
export function toggleLanguageSelectorDialog() {
    return function(dispatch) {
        dispatch(toggleDialog(LanguageSelectorDialog));
    };
}

/**
 * Signals that the local user has enabled or disabled the subtitles.
 *
 * @param {boolean} enabled - The new state of the subtitles.
 * @param {boolean} displaySubtitles - Whether to display subtitles or not.
 * @param {string} language - The language of the subtitles.
 * @returns {{
 *    type: SET_REQUESTING_SUBTITLES,
 *    enabled: boolean,
 *    displaySubtitles: boolean,
 *    language: string
 * }}
 */
export function setRequestingSubtitles(
        enabled: boolean,
        displaySubtitles = true,
        language: string | null = `translation-languages:${DEFAULT_LANGUAGE}`
) {
    return {
        type: SET_REQUESTING_SUBTITLES,
        displaySubtitles,
        enabled,
        language
    };
}

/**
 * Signals that a transcript has to be removed from the state.
 *
 * @param {string} transcriptMessageID - The message_id to be removed.
 * @returns {{
 *      type: REMOVE_TRANSCRIPT_MESSAGE,
 *      transcriptMessageID: string,
 * }}
 */
export function removeTranscriptMessage(transcriptMessageID: string) {
    return {
        type: REMOVE_TRANSCRIPT_MESSAGE,
        transcriptMessageID
    };
}

/**
 * Signals that a transcript with the given message_id to be added or updated
 * is received.
 *
 * @param {string} transcriptMessageID -The transcript message_id to be updated.
 * @param {Object} newTranscriptMessage - The updated transcript message.
 * @returns {{
 *      type: UPDATE_TRANSCRIPT_MESSAGE,
 *      transcriptMessageID: string,
 *      newTranscriptMessage: Object
 * }}
 */
export function updateTranscriptMessage(
        transcriptMessageID: string,
        newTranscriptMessage: Object
) {
    return {
        type: UPDATE_TRANSCRIPT_MESSAGE,
        transcriptMessageID,
        newTranscriptMessage
    };
}

/**
 * setting default transcription lang to en-US.
 *
 * @param {boolean} _showDefaultTranscriptionLang
 * @returns {{
*      type: SET_DEFAULT_TRANSCRIPTION_LANGUAGE,
*      _showDefaultTranscriptionLang: boolean,
* }}
*/
export function setDefaultTranscriptionLang(_showDefaultTranscriptionLang: boolean) {

    return {
        type: SET_DEFAULT_TRANSCRIPTION_LANGUAGE,
        _showDefaultTranscriptionLang
    };
}

/**
 * Stores latest voice mode state pushed by Jigasi.
 *
 * @param {Object} payload - Voice mode state payload.
 * @returns {{
 *      type: SET_VOICE_MODE_STATE,
 *      payload: Object
 * }}
 */
export function setVoiceModeState(payload: Object) {
    return {
        type: SET_VOICE_MODE_STATE,
        payload
    };
}

/**
 * Stores participant voice capability pushed by Jigasi.
 *
 * @param {Object} payload - Voice capability payload.
 * @returns {{
 *      type: SET_VOICE_CAPABILITY,
 *      payload: Object
 * }}
 */
export function setVoiceCapability(payload: Object) {
    return {
        type: SET_VOICE_CAPABILITY,
        payload
    };
}

/**
 * Stores participant voice catalog pushed by Jigasi.
 *
 * @param {Object} payload - Voice catalog payload.
 * @returns {{
 *      type: SET_VOICE_CATALOG,
 *      payload: Object
 * }}
 */
export function setVoiceCatalog(payload: Object) {
    return {
        type: SET_VOICE_CATALOG,
        payload
    };
}

/**
 * Stores a translated-audio-track announcement pushed by Jigasi.
 *
 * @param {Object} payload - Translated-audio-track payload
 * ({@code speaker_id}, {@code language}, {@code source_ssrc}, {@code utterance_id}, {@code active}).
 * @returns {{
 *      type: SET_TRANSLATED_AUDIO_TRACK,
 *      payload: Object
 * }}
 */
export function setTranslatedAudioTrack(payload: Object) {
    return {
        type: SET_TRANSLATED_AUDIO_TRACK,
        payload
    };
}

/**
 * Sets whether the local listener hears every remote speaker's voice translated (into their selected translation
 * language) instead of the original. A single toggle (see LanguageSelectorDialog), replacing the old per-speaker
 * Original/Translated menu.
 *
 * Local only: subtitles/subscriber.js reacts by publishing this listener's own `translated_voice_speakers`
 * property, which Jicofo pairs with their translation language. It deliberately does NOT send a
 * {@link sendVoiceModeCommand} - that is broadcast to the room and makes the targeted speaker publish
 * `voice_mode` on their own presence, which Jicofo then applies to every listener. That is why it is reserved for
 * a moderator's forced mode.
 *
 * @param {boolean} enabled - Whether translated voice is on.
 * @returns {{
 *      type: SET_TRANSLATED_VOICE_ENABLED,
 *      enabled: boolean
 * }}
 */
export function setTranslatedVoiceEnabled(enabled: boolean) {
    return {
        type: SET_TRANSLATED_VOICE_ENABLED,
        enabled
    };
}

/**
 * Sends a voice mode command over conference endpoint messages.
 *
 * @param {string} participantID - Target participant id.
 * @param {string} voiceMode - Requested voice mode.
 * @param {boolean} force - Whether this command is a moderator override.
 * @returns {Function}
 */
export function sendVoiceModeCommand(
        participantID: string,
        voiceMode: string,
        force: boolean = false,
        options: Object = {}) {
    return (dispatch: Function, getState: Function) => {
        const conference = getState()['features/base/conference']?.conference;
        const isVoiceModeEnabled = getState()['features/base/config']?.transcription?.voiceMode?.enabled !== false;

        if (!conference || !participantID || !voiceMode || !isVoiceModeEnabled) {
            return;
        }

        const senderId = conference.myUserId?.();
        const clientPersistenceId = getOrCreateVoiceClientPersistenceId();
        const commandPayload = {
            type: 'voice-mode-command',
            participant_id: participantID,
            voice_mode: voiceMode,
            force,
            updated_by: senderId
        };

        if (clientPersistenceId) {
            commandPayload.client_persistence_id = clientPersistenceId;
        }

        // Compared against undefined rather than tested for truthiness: an empty id is how the picker says
        // "use the default voice", and a truthiness test would silently drop it.
        if (options?.clonedVoiceId !== undefined) {
            commandPayload.cloned_voice_id = options.clonedVoiceId;
        }

        if (options?.clonedVoiceStatus) {
            commandPayload.cloned_voice_status = options.clonedVoiceStatus;
        }

        conference.sendMessage(commandPayload);

        dispatch(setVoiceModeState({
            participant_id: participantID,
            effective_voice_mode: voiceMode,
            moderator_forced: force,
            updated_by: senderId
        }));
    };
}

/**
 * Records the local participant's microphone for ~10 seconds, creates the voice clone against Inworld from
 * this browser, and tells Jigasi the resulting voice id.
 *
 * @param {string} participantID - Local participant's id.
 * @param {Function} onStateChange - Callback invoked with 'recording'|'uploading'|'done'|'error', a message, and
 * while recording the microphone stream.
 * @returns {Function}
 */
export function enrollVoiceClone(participantID: string, onStateChange: Function) {
    return async (dispatch: Function, getState: Function) => {
        const state = getState();
        const conference = state['features/base/conference']?.conference;
        const isVoiceModeEnabled = state['features/base/config']?.transcription?.voiceMode?.enabled !== false;

        if (!conference || !participantID || !isVoiceModeEnabled) {
            if (onStateChange) {
                onStateChange('error', 'voiceModeNotAvailable');
            }

            return;
        }

        const inworld = getInworldCloneConfig(state);

        if (!inworld) {
            if (onStateChange) {
                onStateChange('error', 'cloningNotConfigured');
            }

            return;
        }

        let stream;

        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });
        } catch {
            if (onStateChange) {
                onStateChange('error', 'microphoneAccessDenied');
            }

            return;
        }

        if (onStateChange) {
            // The stream goes along so the dialog can show a live input level while recording.
            onStateChange('recording', '', stream);
        }

        // Everything past this point is wrapped: an exception here used to escape the thunk as an unhandled
        // rejection, leaving the button stuck on "Recording..." forever with no hint of what went wrong.
        try {
            const chunks = [];
            const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });

            recorder.ondataavailable = e => {
                if (e.data.size > 0) {
                    chunks.push(e.data);
                }
            };

            await new Promise(resolve => {
                recorder.onstop = resolve;
                recorder.start();

                // Inworld recommends a 10-30s clip for clone quality.
                setTimeout(() => recorder.stop(), CLONE_RECORDING_SECONDS * 1000);
            });

            stream.getTracks().forEach(t => t.stop());

            if (onStateChange) {
                onStateChange('uploading');
            }

            const blob = new Blob(chunks, { type: 'audio/webm' });
            const base64Sample = await _blobToBase64(blob);

            if (!base64Sample) {
                throw new Error('empty recording');
            }

            // The clone is created by this browser against Inworld directly. Sending the recording to Jigasi
            // over XMPP is not an option: one stanza carrying it makes the browser fragment the websocket
            // message, prosody's mod_websocket rejects continuation frames and closes the connection, and it
            // also put ten seconds of the user's voice in front of everyone in the room. Only the resulting
            // voice id is sent on, which is small and not sensitive.
            const voiceId = await createInworldClone(inworld, participantID, base64Sample);

            setStoredCloneVoiceId(voiceId);
            setCloneVoiceSelected(true);

            logger.info(`Clone created, voiceId=${voiceId}, sample bytes=${blob.size}`);
            dispatch(announceClonedVoice(participantID));

            if (onStateChange) {
                onStateChange('done');
            }
        } catch (err) {
            logger.error('Voice clone enrollment failed', err);
            stream.getTracks().forEach(t => t.stop());

            if (onStateChange) {
                onStateChange('error', err?.message || 'unknown');
            }
        }
    };
}

/**
 * Base64-encodes a blob.
 *
 * Done via FileReader rather than `btoa(String.fromCharCode(...new Uint8Array(buffer)))`: that spread passes one
 * argument per byte, so a recording of any real length exceeds the engine's argument limit and throws - which is
 * exactly what left enrollment silently dead. FileReader also avoids materializing the whole byte array as a JS
 * string.
 *
 * @param {Blob} blob - The recorded audio.
 * @returns {Promise<string>} the base64 payload, without the `data:...;base64,` prefix.
 */
function _blobToBase64(blob: Object) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onerror = () => reject(reader.error || new Error('could not read recording'));
        reader.onload = () => {
            const result = String(reader.result || '');
            const commaIndex = result.indexOf(',');

            resolve(commaIndex === -1 ? '' : result.substring(commaIndex + 1));
        };
        reader.readAsDataURL(blob);
    });
}

/**
 * Creates a voice clone against Inworld from the browser.
 *
 * Mirrors the body Jigasi sends on its own clone path, so the two cannot drift apart. Inworld's current docs
 * describe a `languageCode` field instead of `langCode` and prefer WAV/MP3 over webm; neither spelling has ever
 * been exercised against the live API, so this keeps the server's shape and reports whatever the API answers
 * verbatim rather than guessing.
 *
 * @param {{apiKey: string, baseUrl: string}} inworld - Inworld settings.
 * @param {string} participantID - Local participant's id, used for the clone's display name.
 * @param {string} base64Sample - The recording, base64 without a data URL prefix.
 * @returns {Promise<string>} The created voice id.
 */
async function createInworldClone(inworld, participantID, base64Sample) {
    const sanitizedId = String(participantID).replace(/[^a-zA-Z0-9_-]/g, '')
        .toLowerCase();
    const displayName = `meethour_${sanitizedId}`;
    const baseUrl = inworld.baseUrl.endsWith('/') ? inworld.baseUrl.slice(0, -1) : inworld.baseUrl;

    const response = await fetch(`${baseUrl}${INWORLD_CLONE_PATH}`, {
        method: 'POST',
        headers: {
            'Authorization': `Basic ${inworld.apiKey}`,
            'Content-Type': 'application/json'
        },

        // No langCode/languageCode: Inworld then detects the recording's language itself, instead of an English
        // clone mispronouncing every other language it is asked to speak.
        body: JSON.stringify({
            displayName,
            voiceSamples: [ { audioData: base64Sample } ]
        })
    });

    const body = await response.text();

    if (!response.ok) {
        throw new Error(`Inworld clone failed (${response.status}): ${body.slice(0, 300)}`);
    }

    let voiceId;

    try {
        voiceId = _extractInworldVoiceId(JSON.parse(body));
    } catch {
        throw new Error(`Inworld clone returned unparseable response: ${body.slice(0, 300)}`);
    }

    if (!voiceId) {
        throw new Error(`Inworld clone returned no voiceId: ${body.slice(0, 300)}`);
    }

    return voiceId;
}

/**
 * Pulls the voice id out of an Inworld clone response.
 *
 * Accepts the same three shapes Jigasi accepts: a top-level `voiceId`, a nested `voice.voiceId`, or the trailing
 * segment of a `workspaces/.../voices/<id>` resource name.
 *
 * @param {Object} json - The parsed response.
 * @returns {string|undefined}
 */
function _extractInworldVoiceId(json) {
    if (!json) {
        return undefined;
    }

    if (typeof json.voiceId === 'string' && json.voiceId) {
        return json.voiceId;
    }

    const voice = json.voice;

    if (!voice) {
        return undefined;
    }

    if (typeof voice.voiceId === 'string' && voice.voiceId) {
        return voice.voiceId;
    }

    if (typeof voice.name === 'string') {
        const index = voice.name.lastIndexOf('/voices/');

        if (index >= 0) {
            return voice.name.substring(index + '/voices/'.length) || undefined;
        }
    }

    return undefined;
}

/**
 * Tells the transcriber which voice this participant's translated speech should use: their stored clone, or the
 * provider default when they picked that instead.
 *
 * The clone id lives in this browser, but Jigasi only holds it in per-session memory, and a room message reaches
 * only whoever is in the room when it is sent. So this has to be repeated every time a transcriber joins - see the
 * `_TRANSCRIBER_JOINED` handling in middleware.js - or a clone made before captions were on, a new call, or the
 * transcriber reconnecting mid-call all silently fall back to the default voice.
 *
 * Sent as a `clone-enroll-command`, which only Jigasi acts on. A `voice-mode-command` would also carry the id, but
 * this participant's own client answers that by publishing `voice_mode` on its presence, and Jicofo applies that to
 * every listener - forcing this speaker's translation on the whole room.
 *
 * @param {string} participantID - Local participant's id.
 * @returns {Function}
 */
export function announceClonedVoice(participantID: string) {
    return (dispatch: Function, getState: Function) => {
        const conference = getState()['features/base/conference']?.conference;
        const voiceId = getStoredCloneVoiceId();

        // No clone was ever made in this browser, so there is nothing to tell the transcriber.
        if (!conference || !participantID || !voiceId) {
            return;
        }

        const useClone = isCloneVoiceSelected();
        const payload = {
            type: 'clone-enroll-command',
            participant_id: participantID,

            // Jigasi treats a missing consent as "use the default voice" and drops its stored mapping, without the
            // Inworld lookup it makes for a clone id - the cheapest way to say "default voice".
            clone_consent_given: useClone ? 'true' : 'false',
            cloned_voice_id: useClone ? voiceId : '',
            updated_by: conference.myUserId?.()
        };
        const clientPersistenceId = getOrCreateVoiceClientPersistenceId();

        if (clientPersistenceId) {
            payload.client_persistence_id = clientPersistenceId;
        }

        logger.info(`Announcing ${useClone ? `cloned voice ${voiceId}` : 'default voice'} to the transcriber`);
        conference.sendMessage(payload);
    };
}

/**
 * Picks whether this participant's translated speech uses their cloned voice or the provider default.
 *
 * @param {string} participantID - Local participant's id.
 * @param {boolean} useClone - True for the cloned voice, false for the default one.
 * @returns {Function}
 */
export function selectClonedVoice(participantID: string, useClone: boolean) {
    return (dispatch: Function) => {
        setCloneVoiceSelected(useClone);
        dispatch(announceClonedVoice(participantID));
    };
}

/**
 * Applies a translation language the local user picked, and tells them what it did.
 *
 * Kept separate from {@link setRequestingSubtitles} so only deliberate choices are announced: captions are also
 * started automatically when the transcriber joins and when a recording begins, and those must stay silent.
 *
 * @param {boolean} enabled - Whether subtitles are on.
 * @param {boolean} displaySubtitles - Whether to render subtitles.
 * @param {string|null} language - The chosen language, or null for "off".
 * @returns {Function}
 */
export function selectTranslationLanguage(enabled, displaySubtitles, language) {
    return (dispatch, getState) => {
        const previousLanguage = getState()['features/subtitles']._language;

        dispatch(setRequestingSubtitles(enabled, displaySubtitles, language));

        if (!language || language === previousLanguage) {
            return;
        }

        const languageName = i18next.t(`languages:${language.replace('translation-languages:', '').split('-')[0]}`);

        dispatch(showNotification({
            titleKey: 'transcribing.translationLanguageChanged',
            descriptionArguments: { language: languageName },
            descriptionKey: 'transcribing.translationLanguageChangedDescription'
        }, NOTIFICATION_TIMEOUT_TYPE.SHORT));

        // Judged against the language just picked rather than the one in the store: this runs in the same tick as
        // the dispatch above, so the store still holds the previous language.
        const reason = getTranslatedVoiceUnavailableReason(getState(), language);

        if (reason) {
            // Off, not just disabled: the listener goes back to the original audio instead of keeping a switch
            // that silently does nothing until they pick a language that has a voice.
            if (reason === 'unsupportedLanguage') {
                dispatch(setTranslatedVoiceEnabled(false));
            }

            // showWarningNotification takes no timeout, so the appearance is set here instead - otherwise this
            // one would sit on screen until dismissed while the first one faded.
            dispatch(showNotification({
                appearance: NOTIFICATION_TYPE.WARNING,
                titleKey: 'transcribing.translatedVoice',
                descriptionKey: `transcribing.translatedVoiceUnavailable.${reason}`
            }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        }
    };
}

/**
 * Handles Jigasi reporting that translated voice cannot be produced in a language (the text-to-speech provider
 * does not speak it, or refused it). When that is the local listener's language and their translated voice is on,
 * it is switched off - which unsubscribes from the silent synthetic source and brings the original audio back - and
 * the listener is told why. Captions are unaffected.
 *
 * @param {string} language - The target language the voice is unavailable in.
 * @returns {Function}
 */
export function translatedVoiceUnavailable(language: string) {
    return (dispatch: Function, getState: Function) => {
        const state = getState();

        if (!language || language !== getSelectedTranslationLanguage(state)
                || !state['features/subtitles']._translatedVoiceEnabled) {
            return;
        }

        dispatch(setTranslatedVoiceEnabled(false));
        dispatch(showNotification({
            appearance: NOTIFICATION_TYPE.WARNING,
            titleKey: 'transcribing.translatedVoice',
            descriptionKey: 'transcribing.translatedVoiceUnavailable.unsupportedLanguage'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
    };
}
